import {
  isValidEmail,
  isValidPhone,
  updatePhoneValueSearch,
} from '@sales-center/common'
import {
  IDateRangeFilter,
  IFilterSet,
  IMultiSelectFilter,
  INullableFilter,
  IOrderBy,
  ITernaryFilter,
  IValueRangeFilter,
  normalizeFilters,
  orderByToArray,
} from '@user/components/Filters'
import { produce } from 'immer'
import { ILead } from './typings'

export interface LeadFilterSet extends IFilterSet<ILead> {
  filters: {
    createdDate: IDateRangeFilter<ILead>
    disqualifiedDate: IDateRangeFilter<ILead>
    nextActionDate: IDateRangeFilter<ILead>
    createdBy: IMultiSelectFilter<ILead>
    submittedBy: IMultiSelectFilter<ILead>
    services: IMultiSelectFilter<ILead>
    salesFunnelId: IMultiSelectFilter<ILead>
    salesFunnelStageId: IMultiSelectFilter<ILead>
    initialValue: IValueRangeFilter<ILead>
    recurringValue: IValueRangeFilter<ILead>
    totalLeadValue: IValueRangeFilter<ILead>
    locationTypeId: IMultiSelectFilter<ILead>
    postalCode: IMultiSelectFilter<ILead>
    billToPostalCode: IMultiSelectFilter<ILead>
    divisionId: IMultiSelectFilter<ILead>
    closed: INullableFilter<ILead>
    assignee: IMultiSelectFilter<ILead>
    salesTeamId: IMultiSelectFilter<ILead>
    salesTeamIds: IMultiSelectFilter<ILead>
    sourceId: IMultiSelectFilter<ILead>
    campaignId: IMultiSelectFilter<ILead>
    activityStatus: IMultiSelectFilter<ILead>
    activityTypeId: IMultiSelectFilter<ILead>
    type: IMultiSelectFilter<ILead>
    importId: IMultiSelectFilter<ILead>
    targetPests: IMultiSelectFilter<ILead>
    agreementStatuses: IMultiSelectFilter<ILead>
    hasPestPacLocation: ITernaryFilter<ILead>
    knockStatusSettingId: IMultiSelectFilter<ILead>
  }
}

//#region sorting
export const defaultOrderBy: string = 'createdDate desc,id desc'

export const orderByOptions: IOrderBy[] = [
  {
    label: 'Created Date (Newest - Oldest)',
    value: 'createdDate desc,id desc',
  },
  { label: 'Created Date (Oldest - Newest)', value: 'createdDate asc,id asc' },
  { label: 'Next Action Date (Newest - Oldest)', value: 'nextActionDate desc' },
  { label: 'Next Action Date (Oldest - Newest)', value: 'nextActionDate asc' },
  { label: 'Contact Name (A-Z)', value: 'leadName asc' },
  { label: 'Contact Name (Z-A)', value: 'leadName desc' },
  { label: 'Total Value (High - Low)', value: 'totalLeadValue desc' },
  { label: 'Total Value (Low - High)', value: 'totalLeadValue asc' },
]

export const sortOptionArray = orderByToArray(orderByOptions)
//#endregion

export const defaultFilters = normalizeFilters<ILead, LeadFilterSet>({
  appliedCount: 0,
  normalizedFilters: [],
  orderBy: defaultOrderBy,
  filters: {
    createdDate: {
      propertyName: 'createdDate',
      type: 'dateRange',
      value: { matchType: 'bt', start: null, end: null },
    },
    disqualifiedDate: {
      propertyName: 'closedDate',
      type: 'dateRange',
      value: { matchType: 'bt', start: null, end: null },
    },
    nextActionDate: {
      propertyName: 'nextActionDate',
      type: 'dateRange',
      value: { matchType: 'bt', start: null, end: null },
    },
    createdBy: {
      propertyName: 'createdBy',
      type: 'multiSelect',
      nestedPropertyName: 'id',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    submittedBy: {
      propertyName: 'submittedBy',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    assignee: {
      propertyName: 'assignee',
      type: 'multiSelect',
      nestedPropertyName: 'id',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    services: {
      propertyName: 'services',
      type: 'multiSelect',
      nestedPropertyName: 'id',
      propertyIsArray: true,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    activityStatus: {
      propertyName: 'activities',
      type: 'multiSelect',
      nestedPropertyName: 'status',
      propertyIsArray: true,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    activityTypeId: {
      propertyName: 'activities',
      type: 'multiSelect',
      nestedPropertyName: 'ActivityTypeId',
      propertyIsArray: true,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    salesFunnelId: {
      propertyName: 'salesFunnelId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    salesFunnelStageId: {
      propertyName: 'salesFunnelStageId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    initialValue: {
      propertyName: 'initialValue',
      type: 'valueRange',
      value: { min: '', max: '' },
    },
    recurringValue: {
      propertyName: 'recurringValue',
      type: 'valueRange',
      value: { min: '', max: '' },
    },
    totalLeadValue: {
      propertyName: 'totalLeadValue',
      type: 'valueRange',
      value: { min: '', max: '' },
    },
    locationTypeId: {
      propertyName: 'locationTypeId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    postalCode: {
      propertyName: 'postalCode',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      startsWith: true,
      value: { mode: 'any', options: [] },
    },
    billToPostalCode: {
      propertyName: 'billtoPostalCode',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      startsWith: true,
      value: { mode: 'any', options: [] },
    },
    divisionId: {
      propertyName: 'divisionId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    closed: {
      propertyName: 'closedDate',
      type: 'nullable',
      value: 'null',
    },
    salesTeamId: {
      propertyName: 'salesTeamId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    salesTeamIds: {
      propertyName: 'salesTeamId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    sourceId: {
      propertyName: 'sourceId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    campaignId: {
      propertyName: 'campaignId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    type: {
      propertyName: 'type',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    importId: {
      propertyName: 'importId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    targetPests: {
      propertyName: 'targetPests',
      type: 'multiSelect',
      nestedPropertyName: 'id',
      propertyIsArray: true,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    agreementStatuses: {
      propertyName: 'agreementStatuses',
      type: 'multiSelect',
      nestedPropertyName: 'id',
      propertyIsArray: true,
      propertyIsString: true,
      hideAllMode: true,
      value: { mode: 'any', options: [] },
    },
    hasPestPacLocation: {
      propertyName: 'hasPestPacLocation',
      type: 'ternary',
      value: 'empty',
    },
    knockStatusSettingId: {
      propertyName: 'knockStatusSettingId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
  },
  search: {
    value: '',
    placeholder: 'Name, email, phone, address, location',
    searchFields: [
      {
        propertyNames: [
          'contactName',
          'billToContactName',
          'company',
          'primaryContactSearchName',
        ],
        matchType: 'contains',
      },
      {
        propertyNames: ['externalLocationCode'],
        matchType: 'eq',
        include: /^[\d]+$/,
      },
      {
        propertyNames: [
          'phone',
          'mobilePhone',
          'billToPhone',
          'billToMobilePhone',
          'primaryContactSearchPhone',
          'primaryContactSearchMobile',
        ],
        matchType: 'eq',
        include: isValidPhone,
        formatter: updatePhoneValueSearch,
      },
      {
        propertyNames: [
          'emailAddress',
          'billToEmail',
          'primaryContactSearchPrimaryEmail',
        ],
        matchType: 'eq',
        include: isValidEmail,
      },
      {
        propertyNames: [
          'address',
          'billToAddress',
          'primaryContactSearchAddress',
        ],
        matchType: 'contains',
      },
    ],
  },
})

export const clearedFilters = produce(defaultFilters, (draft) => {
  draft.filters.closed.value = 'empty'
})
